<script lang="ts">
  /*
   * FileMenu — single dropdown that consolidates the file/share/export/edit
   * actions the old header surfaced as separate icons (Open / Copy / Share /
   * Export menu / Clear). Keeps the header to three affordances: this menu,
   * the theme toggle, and Settings.
   *
   * Recents live here too — File is the natural home for "your other
   * documents", same convention as VSCode / Word / Notion. Previously
   * recents were only visible in the EmptyState (when the doc was empty)
   * which made them inaccessible mid-edit. The list is passed in as a
   * prop so it stays in sync with App.svelte's reactive recentsList — the
   * 8s autosave / clearDoc handlers refresh it and the menu picks up
   * the new entries automatically.
   */
  import { Menu, Star } from '@lucide/svelte'
  import { openMarkdownFile, saveMarkdownFile } from '../lib/fileAccess'
  import { exportMarkdown, exportHtml, printDoc } from '../lib/export'
  import { formatShortcut } from '../lib/platform'
  import type { Recent } from '../lib/recents'

  interface Props {
    source: string
    title: string
    recents?: Recent[]
    onLoadSource: (text: string) => void
    onOpenRecent?: (source: string) => void
    onShare: () => void
    onCopy: () => void
    onClear: () => void
  }
  let {
    source,
    title,
    recents = [],
    onLoadSource,
    onOpenRecent,
    onShare,
    onCopy,
    onClear,
  }: Props = $props()

  // Compact relative time (e.g. "now", "5m", "3h", "2d", "Mar 4"). Small
  // enough to inline — no dependency. Threshold falls back to short date
  // beyond 7 days because "23d" stops being useful and an absolute label
  // is clearer.
  function timeAgo(ts: number): string {
    const diff = Math.max(0, Date.now() - ts)
    const s = Math.floor(diff / 1000)
    if (s < 45) return 'now'
    const m = Math.floor(s / 60)
    if (m < 60) return `${m}m`
    const h = Math.floor(m / 60)
    if (h < 24) return `${h}h`
    const d = Math.floor(h / 24)
    if (d < 7) return `${d}d`
    return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  }

  let open = $state(false)

  function close(): void {
    open = false
  }

  function onWindowClick(e: MouseEvent): void {
    if (!open) return
    const target = e.target as HTMLElement
    if (!target.closest('.file-menu')) open = false
  }

  async function openFile(): Promise<void> {
    close()
    const text = await openMarkdownFile()
    if (text !== null) onLoadSource(text)
  }

  async function saveFile(): Promise<void> {
    close()
    await saveMarkdownFile(source, title)
  }

  function shareLink(): void {
    close()
    onShare()
  }

  function copy(): void {
    close()
    onCopy()
  }

  function clear(): void {
    close()
    onClear()
  }

  function openRecent(r: Recent): void {
    close()
    onOpenRecent?.(r.source)
  }

  function exportMd(): void {
    close()
    exportMarkdown(source, title)
  }

  function exportHtmlFile(): void {
    close()
    void exportHtml(source, title)
  }

  function print(): void {
    close()
    printDoc()
  }
</script>

<svelte:window onclick={onWindowClick} />

<div class="file-menu">
  <button
    type="button"
    class="btn-icon"
    title="File menu"
    aria-label="File menu"
    aria-expanded={open}
    aria-haspopup="menu"
    onclick={() => (open = !open)}
  >
    <Menu size={18} strokeWidth={1.75} aria-hidden="true" />
  </button>
  {#if open}
    <div class="menu" role="menu">
      <button type="button" role="menuitem" onclick={openFile}>
        <span>Open file…</span>
      </button>
      <button type="button" role="menuitem" onclick={saveFile}>
        <span>Save file…</span>
        <kbd>{formatShortcut('Ctrl+S')}</kbd>
      </button>
      {#if recents.length > 0 && onOpenRecent}
        <div class="divider" role="separator"></div>
        <div class="section-label" aria-hidden="true">Recent</div>
        {#each recents.slice(0, 5) as r (r.id)}
          <button
            type="button"
            role="menuitem"
            class="recent-item"
            title={r.title}
            onclick={() => openRecent(r)}
          >
            <span class="recent-title">
              {#if r.pinned}
                <Star size={12} strokeWidth={2} fill="currentColor" class="pin" aria-hidden="true" />
              {/if}
              {r.title}
            </span>
            <span class="recent-meta">{timeAgo(r.savedAt)}</span>
          </button>
        {/each}
      {/if}
      <div class="divider" role="separator"></div>
      <button type="button" role="menuitem" onclick={shareLink}>
        <span>Share link…</span>
        <kbd>{formatShortcut('Ctrl+Shift+S')}</kbd>
      </button>
      <button type="button" role="menuitem" onclick={exportMd}>
        <span>Export Markdown</span>
      </button>
      <button type="button" role="menuitem" onclick={exportHtmlFile}>
        <span>Export HTML</span>
      </button>
      <button type="button" role="menuitem" onclick={print}>
        <span>Print / PDF</span>
      </button>
      <div class="divider" role="separator"></div>
      <button type="button" role="menuitem" onclick={copy}>
        <span>Copy markdown</span>
      </button>
      <button type="button" role="menuitem" class="danger" onclick={clear}>
        <span>Clear document</span>
      </button>
    </div>
  {/if}
</div>

<style>
  .file-menu {
    position: relative;
  }
  .btn-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    border-radius: var(--radius-sm);
    background: transparent;
    color: var(--text-secondary);
    cursor: pointer;
  }
  .btn-icon:hover {
    color: var(--text-primary);
    background: var(--bg-surface);
  }
  .btn-icon:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }
  .menu {
    position: absolute;
    right: 0;
    top: calc(100% + 4px);
    min-width: 220px;
    background: var(--bg-elevated);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-lg);
    padding: var(--space-1);
    z-index: 30;
    display: flex;
    flex-direction: column;
  }
  .menu button {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-3);
    text-align: left;
    border: none;
    background: transparent;
    padding: 8px 12px;
    border-radius: var(--radius-sm);
    font: inherit;
    font-size: var(--text-sm);
    color: var(--text-primary);
    cursor: pointer;
  }
  .menu button:hover {
    background: var(--bg-sunken);
  }
  .menu button.danger {
    color: var(--callout-caution);
  }
  .menu button.danger:hover {
    background: color-mix(in srgb, var(--callout-caution) 12%, transparent);
  }
  kbd {
    font-family: var(--font-mono);
    font-size: var(--text-xs);
    color: var(--text-muted);
  }
  .divider {
    height: 1px;
    margin: var(--space-1) 0;
    background: var(--border-subtle);
  }
  .section-label {
    padding: 6px 12px 2px;
    font-size: var(--text-xs);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-muted);
  }
  .recent-item {
    /* Recents reuse the menu button base style, but the title can be
       long so we clamp with ellipsis. The meta column (relative time)
       stays right-aligned and never wraps. */
    gap: var(--space-2) !important;
  }
  .recent-title {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .recent-title :global(.pin) {
    color: var(--accent);
    flex-shrink: 0;
  }
  .recent-meta {
    font-size: var(--text-xs);
    color: var(--text-muted);
    flex-shrink: 0;
    font-variant-numeric: tabular-nums;
  }
</style>
